import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowPath,
  heroCheck,
  heroPencil,
  heroPlus,
  heroTrash,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import { NgpSelectPortal } from 'ng-primitives/select';
import type { AuthPermission } from '../../../core/api/generated/models/AuthPermission';
import { AuthStore } from '../../../core/auth/auth.store';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiCheckbox,
  UiDataTable,
  UiDataTableActions,
  UiDataTableCell,
  UiDataTableEmptyCell,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiDateField,
  UiDialog,
  UiDialogDescription,
  UiDialogOverlay,
  UiDialogTitle,
  UiEmptyText,
  UiEmptyState,
  UiHint,
  UiInput,
  UiLabel,
  UiPanelCard,
  UiPanelText,
  UiPanelTitle,
  UiSectionHeader,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiTextarea,
  UiPageHeader,
} from '../../../shared/ui';
import {
  SettingsCoreApiService,
  type BusinessHoursInterval,
  type CoreBusiness,
  type CoreBusinessHoursCalendar,
  type CoreBusinessPreferenceOptions,
  type CoreBusinessSettings,
} from '../settings-core-api.service';

type CalendarHoliday = CoreBusinessHoursCalendar['holidays'][number];
type EditableHoliday = { id: string; date: string; label: string };
type PreferenceKind = keyof CoreBusinessPreferenceOptions;

const WEEK_DAYS: readonly { index: number; short: string; label: string }[] = [
  { index: 0, short: 'Seg', label: 'Segunda' },
  { index: 1, short: 'Ter', label: 'Terça' },
  { index: 2, short: 'Qua', label: 'Quarta' },
  { index: 3, short: 'Qui', label: 'Quinta' },
  { index: 4, short: 'Sex', label: 'Sexta' },
  { index: 5, short: 'Sáb', label: 'Sábado' },
  { index: 6, short: 'Dom', label: 'Domingo' },
];

@Component({
  selector: 'app-general-settings-page',
  imports: [
    NgIcon,
    NgpSelectPortal,
    ReactiveFormsModule,
    UiBadge,
    UiButton,
    UiCheckbox,
    UiDataTable,
    UiDataTableActions,
    UiDataTableCell,
    UiDataTableEmptyCell,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiDateField,
    UiDialog,
    UiDialogDescription,
    UiDialogOverlay,
    UiDialogTitle,
    UiEmptyText,
    UiEmptyState,
    UiHint,
    UiInput,
    UiLabel,
    UiPageHeader,
    UiPanelCard,
    UiPanelText,
    UiPanelTitle,
    UiSectionHeader,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiTextarea,
  ],
  providers: [
    provideIcons({
      heroArrowPath,
      heroCheck,
      heroPencil,
      heroPlus,
      heroTrash,
      heroXMark,
    }),
  ],
  templateUrl: './general-settings.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeneralSettingsPage {
  private readonly api = inject(SettingsCoreApiService);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly toastr = inject(ToastrService);

  readonly weekDays = WEEK_DAYS;
  readonly business = signal<CoreBusiness | null>(null);
  readonly settings = signal<CoreBusinessSettings | null>(null);
  readonly calendars = signal<CoreBusinessHoursCalendar[]>([]);
  readonly options = signal<CoreBusinessPreferenceOptions>({
    locales: [],
    timezones: [],
    currencies: [],
  });
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly creatingCalendar = signal(false);
  readonly editingCalendarId = signal('');
  readonly deletingCalendarId = signal('');
  readonly calendarHolidays = signal<EditableHoliday[]>([]);
  readonly calendarWorkingDays = signal<number[]>([0, 1, 2, 3, 4]);
  readonly businessId = computed(() => this.authStore.business()?.id ?? null);
  readonly canUpdateBusiness = computed(() => this.hasPermission('core:business:update'));
  readonly writePermissionHint =
    'Você precisa da permissão core:business:update para alterar estas configurações.';
  readonly businessPaused = computed(() => this.business()?.status === 'disabled');
  readonly holidayRowsInvalid = computed(() =>
    this.calendarHolidays().some((holiday) => !isValidDateInput(holiday.date)),
  );
  readonly activeCalendars = computed(() =>
    this.calendars().filter((calendar) => calendar.isActive),
  );
  readonly editingCalendar = computed(
    () => this.calendars().find((calendar) => calendar.id === this.editingCalendarId()) ?? null,
  );
  readonly deletingCalendar = computed(
    () => this.calendars().find((calendar) => calendar.id === this.deletingCalendarId()) ?? null,
  );

  readonly businessForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
  });

  readonly settingsForm = this.fb.nonNullable.group({
    locale: ['pt-BR', [Validators.required, Validators.maxLength(20)]],
    timezone: ['America/Sao_Paulo', [Validators.required, Validators.maxLength(80)]],
    defaultCurrency: [
      'BRL',
      [Validators.required, Validators.minLength(3), Validators.maxLength(3)],
    ],
  });

  readonly calendarForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    description: [''],
    timezone: ['America/Sao_Paulo', [Validators.required, Validators.maxLength(80)]],
    opensAt: ['09:00', [Validators.required]],
    closesAt: ['18:00', [Validators.required]],
    isDefault: [false],
  });

  private nextHolidayId = 0;

  constructor() {
    this.load();
  }

  load() {
    const businessId = this.businessId();
    if (!businessId) return;

    this.loading.set(true);
    forkJoin({
      business: this.api.getBusiness(businessId),
      settings: this.api.getBusinessSettings(businessId),
      calendars: this.api.listBusinessHoursCalendars(businessId),
      options: this.api.getBusinessPreferenceOptions(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ business, settings, calendars, options }) => {
          this.business.set(business);
          this.settings.set(settings);
          this.calendars.set(calendars.calendars.filter((calendar) => calendar.isActive));
          this.options.set(options);
          this.businessForm.patchValue({ name: business.name });
          this.settingsForm.patchValue({
            locale: this.optionValue('locales', settings.locale, 'pt-BR'),
            timezone: this.optionValue('timezones', settings.timezone, 'America/Sao_Paulo'),
            defaultCurrency: this.optionValue('currencies', settings.defaultCurrency, 'BRL'),
          });
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toastr.error('Não foi possível carregar as configurações gerais.');
        },
      });
  }

  saveBusiness() {
    const businessId = this.businessId();
    if (!businessId || this.businessForm.invalid) return;
    if (!this.requireUpdatePermission()) return;

    this.saving.set(true);
    this.api
      .updateBusiness(businessId, { name: this.businessForm.controls.name.value.trim() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (business) => {
          this.business.set(business);
          this.authStore.updateCurrentBusiness({ name: business.name, status: business.status });
          this.saving.set(false);
          this.toastr.success('Organização atualizada.');
        },
        error: () => {
          this.saving.set(false);
          this.toastr.error('Não foi possível atualizar a organização.');
        },
      });
  }

  saveSettings() {
    const businessId = this.businessId();
    if (!businessId || this.settingsForm.invalid) return;
    if (!this.requireUpdatePermission()) return;

    const value = this.settingsForm.getRawValue();
    const settings = {
      locale: this.optionValue('locales', value.locale, 'pt-BR'),
      timezone: this.optionValue('timezones', value.timezone, 'America/Sao_Paulo'),
      defaultCurrency: this.optionValue('currencies', value.defaultCurrency, 'BRL'),
    };

    this.settingsForm.patchValue(settings);
    this.saving.set(true);
    this.api
      .updateBusinessSettings(businessId, settings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.settings.set(settings);
          this.saving.set(false);
          this.toastr.success('Preferências salvas.');
        },
        error: () => {
          this.saving.set(false);
          this.toastr.error('Não foi possível salvar as preferências.');
        },
      });
  }

  reactivateBusiness() {
    const businessId = this.businessId();
    if (!businessId) return;
    if (!this.requireUpdatePermission()) return;

    this.saving.set(true);
    this.api
      .updateBusiness(businessId, { status: 'active' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (business) => {
          this.business.set(business);
          this.authStore.updateCurrentBusiness({ status: business.status });
          this.saving.set(false);
          this.toastr.success('Organização reativada.');
        },
        error: () => {
          this.saving.set(false);
          this.toastr.error('Não foi possível reativar a organização.');
        },
      });
  }

  openCreateCalendar() {
    if (!this.requireUpdatePermission()) return;

    this.editingCalendarId.set('');
    this.calendarWorkingDays.set([0, 1, 2, 3, 4]);
    this.calendarHolidays.set([]);
    this.calendarForm.reset({
      name: '',
      description: '',
      timezone: this.defaultCalendarTimezone(),
      opensAt: '09:00',
      closesAt: '18:00',
      isDefault: this.activeCalendars().length === 0,
    });
    this.creatingCalendar.set(true);
  }

  openEditCalendar(calendar: CoreBusinessHoursCalendar) {
    if (!this.requireUpdatePermission()) return;

    const firstInterval = calendar.weeklySchedule.flat().find((interval) => interval);
    this.creatingCalendar.set(false);
    this.editingCalendarId.set(calendar.id);
    this.calendarHolidays.set(
      toEditableHolidays(calendar.holidays, () => this.allocateHolidayId()),
    );
    this.calendarWorkingDays.set(
      calendar.weeklySchedule
        .map((intervals, index) => (intervals.length > 0 ? index : -1))
        .filter((index) => index >= 0),
    );
    this.calendarForm.reset({
      name: calendar.name,
      description: calendar.description ?? '',
      timezone: calendar.timezone || this.defaultCalendarTimezone(),
      opensAt: firstInterval?.start ?? '09:00',
      closesAt: firstInterval?.end ?? '18:00',
      isDefault: calendar.isDefault,
    });
  }

  closeCalendarDialog() {
    this.creatingCalendar.set(false);
    this.editingCalendarId.set('');
  }

  saveCalendar() {
    const businessId = this.businessId();
    if (!businessId || this.calendarForm.invalid) return;
    if (!this.requireUpdatePermission()) return;

    const payload = this.calendarPayload();
    if (!payload) return;

    const editingCalendarId = this.editingCalendarId();
    this.saving.set(true);
    const request = editingCalendarId
      ? this.api.updateBusinessHoursCalendar(businessId, editingCalendarId, payload)
      : this.api.createBusinessHoursCalendar(businessId, payload);

    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (calendar) => {
        this.calendars.update((items) => {
          const withoutPrevious = items.filter((item) => item.id !== calendar.id);
          const next = calendar.isActive ? [calendar, ...withoutPrevious] : withoutPrevious;
          return calendar.isDefault
            ? next.map((item) => ({ ...item, isDefault: item.id === calendar.id }))
            : next;
        });
        this.syncDefaultCalendar(calendar);
        this.closeCalendarDialog();
        this.saving.set(false);
        this.toastr.success(editingCalendarId ? 'Calendário atualizado.' : 'Calendário criado.');
      },
      error: () => {
        this.saving.set(false);
        this.toastr.error('Não foi possível salvar o calendário.');
      },
    });
  }

  openDeleteCalendar(calendar: CoreBusinessHoursCalendar) {
    if (!this.requireUpdatePermission()) return;
    this.deletingCalendarId.set(calendar.id);
  }

  closeDeleteCalendar() {
    this.deletingCalendarId.set('');
  }

  confirmDeleteCalendar() {
    const calendar = this.deletingCalendar();
    const businessId = this.businessId();
    if (!calendar || !businessId) return;
    if (!this.requireUpdatePermission()) return;

    this.saving.set(true);
    this.api
      .deleteBusinessHoursCalendar(businessId, calendar.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.calendars.update((items) => items.filter((item) => item.id !== calendar.id));
          if (this.settings()?.defaultBusinessHoursCalendarId === calendar.id) {
            this.settings.update((settings) =>
              settings ? { ...settings, defaultBusinessHoursCalendarId: null } : settings,
            );
          }
          this.closeDeleteCalendar();
          this.saving.set(false);
          this.toastr.success('Calendário excluído.');
        },
        error: () => {
          this.saving.set(false);
          this.toastr.error('Não foi possível excluir o calendário.');
        },
      });
  }

  toggleCalendarDay(dayIndex: number, checked: boolean) {
    if (!this.canUpdateBusiness()) return;

    this.calendarWorkingDays.update((days) => {
      const next = checked
        ? [...new Set([...days, dayIndex])]
        : days.filter((day) => day !== dayIndex);
      return next.sort((left, right) => left - right);
    });
  }

  selectLocale(value: unknown) {
    if (!this.canUpdateBusiness()) return;

    this.settingsForm.controls.locale.setValue(
      this.optionValue('locales', readSelectString(value), 'pt-BR'),
    );
  }

  selectTimezone(value: unknown) {
    if (!this.canUpdateBusiness()) return;

    this.settingsForm.controls.timezone.setValue(
      this.optionValue('timezones', readSelectString(value), 'America/Sao_Paulo'),
    );
  }

  selectCurrency(value: unknown) {
    if (!this.canUpdateBusiness()) return;

    this.settingsForm.controls.defaultCurrency.setValue(
      this.optionValue('currencies', readSelectString(value), 'BRL'),
    );
  }

  selectCalendarTimezone(value: unknown) {
    if (!this.canUpdateBusiness()) return;

    this.calendarForm.controls.timezone.setValue(
      this.optionValue('timezones', readSelectString(value), this.defaultCalendarTimezone()),
    );
  }

  optionLabel(kind: PreferenceKind, value: string) {
    return this.options()[kind].find((option) => option.value === value)?.label ?? value;
  }

  calendarTimezoneLabel(calendar: CoreBusinessHoursCalendar) {
    return this.optionLabel('timezones', calendar.timezone);
  }

  calendarScheduleSummary(calendar: CoreBusinessHoursCalendar) {
    const openDays = calendar.weeklySchedule
      .map((intervals, index) => (intervals.length > 0 ? WEEK_DAYS[index]?.short : ''))
      .filter((day) => day);
    const firstInterval = calendar.weeklySchedule.flat().find((interval) => interval);

    if (!openDays.length || !firstInterval) return 'Fechado todos os dias';
    return `${openDays.join(', ')} · ${firstInterval.start} às ${firstInterval.end}`;
  }

  holidayCount(calendar: CoreBusinessHoursCalendar) {
    return calendar.holidays?.length ?? 0;
  }

  holidayDateInvalid(holiday: EditableHoliday) {
    return holiday.date.length > 0 && !isValidDateInput(holiday.date);
  }

  calendarTimeInvalid() {
    const value = this.calendarForm.getRawValue();
    return value.opensAt.length > 0 && value.closesAt.length > 0 && value.opensAt >= value.closesAt;
  }

  addHolidayRow() {
    if (!this.canUpdateBusiness()) return;

    this.calendarHolidays.update((holidays) => [
      ...holidays,
      { id: this.allocateHolidayId(), date: '', label: '' },
    ]);
  }

  updateHolidayDate(id: string, date: string) {
    if (!this.canUpdateBusiness()) return;

    this.calendarHolidays.update((holidays) =>
      holidays.map((holiday) => (holiday.id === id ? { ...holiday, date } : holiday)),
    );
  }

  updateHolidayLabel(id: string, label: string) {
    if (!this.canUpdateBusiness()) return;

    this.calendarHolidays.update((holidays) =>
      holidays.map((holiday) => (holiday.id === id ? { ...holiday, label } : holiday)),
    );
  }

  removeHolidayRow(id: string) {
    if (!this.canUpdateBusiness()) return;

    this.calendarHolidays.update((holidays) => holidays.filter((holiday) => holiday.id !== id));
  }

  private defaultCalendarTimezone() {
    return this.optionValue(
      'timezones',
      this.settings()?.timezone || this.settingsForm.controls.timezone.value,
      'America/Sao_Paulo',
    );
  }

  private optionValue(kind: PreferenceKind, value: string | null | undefined, fallback: string) {
    const options = this.options()[kind];
    if (value && options.some((option) => option.value === value)) return value;
    if (options.some((option) => option.value === fallback)) return fallback;
    return options[0]?.value ?? fallback;
  }

  private calendarPayload() {
    const value = this.calendarForm.getRawValue();
    if (this.calendarWorkingDays().length === 0) {
      this.toastr.error('Selecione pelo menos um dia de funcionamento.');
      return null;
    }

    if (value.opensAt >= value.closesAt) {
      this.toastr.error('O horário de abertura precisa ser anterior ao fechamento.');
      return null;
    }

    const holidays = parseHolidayRows(this.calendarHolidays());
    if (!holidays) {
      this.toastr.error('Revise os feriados. Toda linha precisa ter uma data válida.');
      return null;
    }

    return {
      name: value.name.trim(),
      description: nullableValue(value.description),
      timezone: value.timezone,
      weeklySchedule: buildWeeklySchedule(
        this.calendarWorkingDays(),
        value.opensAt,
        value.closesAt,
      ),
      holidays,
      exceptions: [],
      isDefault: value.isDefault,
      isActive: true,
    };
  }

  private hasPermission(permission: AuthPermission) {
    return this.authStore.hasEveryPermission([permission]);
  }

  private requireUpdatePermission() {
    if (this.canUpdateBusiness()) {
      return true;
    }

    this.toastr.error(this.writePermissionHint);
    return false;
  }

  private allocateHolidayId() {
    this.nextHolidayId += 1;
    return `holiday-${this.nextHolidayId}`;
  }

  private syncDefaultCalendar(calendar: CoreBusinessHoursCalendar) {
    const businessId = this.businessId();
    if (!businessId || !calendar.isDefault) return;

    this.api
      .updateBusinessSettings(businessId, { defaultBusinessHoursCalendarId: calendar.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => this.settings.set(settings),
        error: () => this.toastr.error('Não foi possível marcar o calendário como padrão.'),
      });
  }
}

function nullableValue(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function buildWeeklySchedule(
  days: number[],
  opensAt: string,
  closesAt: string,
): BusinessHoursInterval[][] {
  const selectedDays = new Set(days);
  return Array.from({ length: 7 }, (_, index) =>
    selectedDays.has(index) ? [{ start: opensAt, end: closesAt }] : [],
  );
}

function parseHolidayRows(rows: EditableHoliday[]): CalendarHoliday[] | null {
  const holidays: CalendarHoliday[] = [];

  for (const row of rows) {
    const date = row.date.trim();
    const label = row.label.trim();
    if (!date && !label) continue;
    if (!isValidDateInput(date)) return null;
    holidays.push({ date, ...(label ? { label } : {}) });
  }

  return holidays;
}

function toEditableHolidays(holidays: CalendarHoliday[], nextId: () => string): EditableHoliday[] {
  return holidays.map((holiday) => ({
    id: nextId(),
    date: holiday.date,
    label: holiday.label ?? '',
  }));
}

function isValidDateInput(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function readSelectString(value: unknown) {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : '';
  }

  return typeof value === 'string' ? value : '';
}
